import { useState } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';
import { useRouter } from 'expo-router';
import { SafeAreaView } from 'react-native-safe-area-context';
import { ArrowLeft, Camera, CheckCircle2, FileImage, UploadCloud, XCircle } from 'lucide-react-native';
import { AppButton } from '../components/ui/AppButton';
import { AppCard } from '../components/ui/AppCard';
import { AppText } from '../components/ui/AppText';
import { showAppToast } from '../components/ui/AppToast';
import { tokens } from '../theme/tokens';

type UploadStatus = 'idle' | 'uploading' | 'success' | 'failed';

export default function ExamUploadPage() {
  const router = useRouter();
  const [status, setStatus] = useState<UploadStatus>('idle');

  const startDemoUpload = () => {
    setStatus('uploading');
    setTimeout(() => setStatus('success'), 500);
  };

  return (
    <SafeAreaView style={styles.screen}>
      <View style={styles.header}>
        <AppButton
          label="试卷"
          variant="ghost"
          onPress={() => router.back()}
          leftSlot={<ArrowLeft size={18} color={tokens.colors.primary} />}
          style={styles.backButton}
        />
        <View style={styles.headerCopy}>
          <AppText variant="title" weight="bold">
            上传试卷
          </AppText>
          <AppText variant="caption" tone="muted">
            ExamUploadScreen · 拍照/相册/待批改状态
          </AppText>
        </View>
      </View>

      <ScrollView contentContainerStyle={styles.content}>
        <AppCard style={styles.uploadCard}>
          <View style={styles.uploadIcon}>
            <UploadCloud size={28} color={tokens.colors.primary} />
          </View>
          <AppText variant="subtitle" weight="bold" align="center">
            选择试卷或答题卡
          </AppText>
          <AppText variant="body" tone="muted" align="center">
            真实接口接入前，先用本地 demo 模拟上传和批改状态。
          </AppText>
          <View style={styles.actionRow}>
            <AppButton
              label="拍照上传"
              onPress={startDemoUpload}
              leftSlot={<Camera size={18} color={tokens.colors.white} />}
              style={styles.actionButton}
            />
            <AppButton
              label="相册选择"
              variant="secondary"
              onPress={startDemoUpload}
              leftSlot={<FileImage size={18} color={tokens.colors.primary} />}
              style={styles.actionButton}
            />
          </View>
          <AppButton
            label="模拟失败"
            variant="secondary"
            onPress={() => {
              setStatus('failed');
              showAppToast('已切到上传失败状态', 'info');
            }}
          />
        </AppCard>

        <AppCard style={styles.statusCard}>
          {status === 'success' ? (
            <CheckCircle2 size={22} color={tokens.colors.success} />
          ) : status === 'failed' ? (
            <XCircle size={22} color={tokens.colors.danger} />
          ) : (
            <UploadCloud size={22} color={tokens.colors.primary} />
          )}
          <View style={styles.statusCopy}>
            <AppText variant="body" weight="bold">
              {status === 'idle'
                ? '等待上传'
                : status === 'uploading'
                  ? '待批改'
                  : status === 'success'
                    ? '批改完成'
                    : '上传失败'}
            </AppText>
            <AppText variant="caption" tone="muted">
              {status === 'failed' ? '可重新上传恢复流程' : '上传、批改中、成功、失败都有 UI'}
            </AppText>
          </View>
        </AppCard>

        <AppButton
          label="查看批改结果"
          disabled={status !== 'success'}
          onPress={() => router.push('/exam-result')}
        />
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: tokens.colors.background,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.md,
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.md,
    paddingBottom: tokens.spacing.sm,
  },
  backButton: {
    minHeight: 40,
    paddingHorizontal: tokens.spacing.sm,
  },
  headerCopy: {
    flex: 1,
  },
  content: {
    paddingHorizontal: tokens.layout.screenPadding,
    paddingTop: tokens.spacing.md,
    paddingBottom: tokens.spacing.xxl,
    gap: tokens.spacing.lg,
  },
  uploadCard: {
    alignItems: 'center',
    gap: tokens.spacing.md,
  },
  uploadIcon: {
    width: 64,
    height: 64,
    borderRadius: tokens.radius.full,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: tokens.colors.surfaceMuted,
  },
  actionRow: {
    flexDirection: 'row',
    gap: tokens.spacing.md,
  },
  actionButton: {
    flex: 1,
  },
  statusCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.md,
  },
  statusCopy: {
    flex: 1,
    gap: tokens.spacing.xs,
  },
});
